import { useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../api/client';
import { useAuth } from '../auth/auth-context';
import { Tag } from '../components/Tag';
import { DisparoDeJornadas, type ModoDeDisparo } from '../components/DisparoDeJornadas';
import {
  Campo,
  Carregando,
  ErroBox,
  Modal,
  ModalRodape,
  PageHeader,
  Paginacao,
  Vazio,
} from '../components/ui';

interface Jornada {
  id: string;
  status: string;
  startedAt: string | null;
  lastInboundAt: string | null;
  lastOutboundAt: string | null;
  reminderCount: number;
  /** Quando o lembrete desta rodada saiu. */
  reminderSentAt: string | null;
  /** Quando ele vai sair, se ainda estiver na fila. */
  reminderDueAt: string | null;
  errorMessage: string | null;
  acoes: string[];
  participant: {
    id: string;
    jobTitle: string | null;
    company: string | null;
    person: { id: string; fullName: string; optOutAt: string | null };
  };
  schedule: { scheduledFor: string; dispatchedAt: string | null; cancelledAt: string | null } | null;
}

interface Resumo {
  porEstado: Record<string, number>;
  semJornada: number;
  total: number;
  ativas: number;
}

/**
 * Rótulo e cor por estado.
 *
 * A cor diz o que fazer, não o que aconteceu: cinza é espera, azul é a
 * plataforma trabalhando, verde é sucesso, vermelho pede alguém.
 */
export const ESTADOS: Record<string, { rotulo: string; cor: string }> = {
  NAO_INICIADA: { rotulo: 'Não iniciada', cor: 'muted' },
  AGENDADA: { rotulo: 'Agendada', cor: 'info' },
  INICIANDO: { rotulo: 'Enviando convite', cor: 'info' },
  CONVITE_ENVIADO: { rotulo: 'Convite enviado', cor: 'info' },
  AGUARDANDO_RESPOSTA: { rotulo: 'Sem resposta', cor: 'warning' },
  EM_ANDAMENTO: { rotulo: 'Conversando', cor: 'success' },
  INATIVO_TEMPORARIO: { rotulo: 'Cobrado, sem resposta', cor: 'warning' },
  CONCLUIDA: { rotulo: 'Concluída', cor: 'success' },
  PAUSADA: { rotulo: 'Pausada', cor: 'muted' },
  CANCELADA: { rotulo: 'Cancelada', cor: 'muted' },
  ERRO_ENVIO: { rotulo: 'Erro de envio', cor: 'danger' },
  NUMERO_INVALIDO: { rotulo: 'Número inválido', cor: 'danger' },
  OPT_OUT: { rotulo: 'Saiu da lista', cor: 'danger' },
  BLOQUEADO_WHATSAPP: { rotulo: 'Bloqueou o número', cor: 'danger' },
};

const rotulo = (estado: string) => ESTADOS[estado]?.rotulo ?? estado;

/**
 * "hoje · 12:53", "amanhã · 12:52", "18/10 · 09:10".
 *
 * O dia por extenso quando é perto, porque é o que se quer saber de relance —
 * "18/10/2026, 12:53:11" obrigava a fazer a conta. Segundos não dizem nada aqui.
 */
function quando(data: string) {
  const d = new Date(data);
  const hora = d.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' });

  const meiaNoite = (x: Date) => new Date(x.getFullYear(), x.getMonth(), x.getDate()).getTime();
  const dias = Math.round((meiaNoite(d) - meiaNoite(new Date())) / 86_400_000);
  const perto: Record<number, string> = { [-1]: 'ontem', 0: 'hoje', 1: 'amanhã' };

  const dia =
    perto[dias] ??
    d.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
    });

  return `${dia} · ${hora}`;
}

/** O quê em cima, quando embaixo — as duas colunas de tempo da tabela. */
function CelulaDeTempo({ topo, data }: { topo: ReactNode; data?: string | null }) {
  return (
    <div className="celula-tempo">
      {topo}
      {data && <span className="celula-tempo-hora">{quando(data)}</span>}
    </div>
  );
}

function UltimaTroca({ jornada: j }: { jornada: Jornada }) {
  if (j.lastInboundAt) {
    return (
      <CelulaDeTempo
        topo={
          <span className="celula-tempo-rotulo">
            <i className="ti ti-message-circle" style={{ color: 'var(--success)' }} /> Respondeu
          </span>
        }
        data={j.lastInboundAt}
      />
    );
  }

  if (j.lastOutboundAt) {
    return (
      <CelulaDeTempo
        topo={
          <span className="celula-tempo-rotulo">
            <i className="ti ti-send" /> Enviamos
          </span>
        }
        data={j.lastOutboundAt}
      />
    );
  }

  return <span className="text-dim">—</span>;
}

/**
 * O lembrete saiu ou não — com a hora.
 *
 * Era a contagem, e a contagem não respondia a pergunta: ela zera quando a
 * pessoa responde, então quem foi cobrado e depois tocou no botão aparecia com
 * "0", como se nunca tivesse recebido nada (18/10/2026).
 */
function Lembrete({ jornada: j }: { jornada: Jornada }) {
  if (j.reminderSentAt) {
    return (
      <CelulaDeTempo
        topo={
          <Tag type="success" icon="ti-check">
            Enviado
          </Tag>
        }
        data={j.reminderSentAt}
      />
    );
  }

  if (j.reminderDueAt) {
    return (
      <CelulaDeTempo
        topo={
          <Tag type="info" icon="ti-clock">
            Agendado
          </Tag>
        }
        data={j.reminderDueAt}
      />
    );
  }

  return <Tag type="muted">Não enviado</Tag>;
}

/**
 * Jornadas de um evento.
 *
 * É a tela que dispara mensagem para gente de verdade — e não tem desfazer.
 * Daí duas escolhas: "iniciar" pede confirmação com o número de pessoas escrito
 * por extenso, e o que foi recusado aparece com o motivo, participante por
 * participante, em vez de um "erro ao iniciar".
 */
export function Jornadas() {
  const { id: eventId } = useParams<{ id: string }>();
  const cliente = useQueryClient();
  const { pode } = useAuth();

  const [pagina, setPagina] = useState(1);
  const [filtro, setFiltro] = useState('');
  const [confirmando, setConfirmando] = useState<ModoDeDisparo | null>(null);
  const [reabrindo, setReabrindo] = useState<{ id: string; nome: string } | null>(null);
  const [motivo, setMotivo] = useState('');

  const chave = ['jornadas', eventId, pagina, filtro];

  /**
   * Enquanto há convite saindo, a tela se atualiza sozinha.
   *
   * "Enviando convite" dura segundos — é o tempo de a fila mandar e a Meta
   * responder. Sem isto a tela congelava nesse estado (18/10/2026: o Gabriel viu
   * "Enviando convite" num convite já entregue, e noutro que a Meta tinha
   * barrado). Fora disso, quem quer ver o que mudou usa o "Atualizar".
   */
  const ACOMPANHAR = 4000;

  const consulta = useQuery({
    queryKey: chave,
    queryFn: () =>
      api<{ data: Jornada[]; meta: { page: number; perPage: number; total: number; totalPages: number } }>(
        `/events/${eventId}/journeys?page=${pagina}${filtro ? `&status=${filtro}` : ''}`,
      ),
    refetchInterval: (q) =>
      q.state.data?.data.some((j) => j.status === 'INICIANDO') ? ACOMPANHAR : false,
  });

  const resumo = useQuery({
    queryKey: ['jornadas', eventId, 'resumo'],
    queryFn: () => api<Resumo>(`/events/${eventId}/journeys/resumo`),
    refetchInterval: (q) => ((q.state.data?.porEstado.INICIANDO ?? 0) > 0 ? ACOMPANHAR : false),
  });

  const invalidar = () => {
    cliente.invalidateQueries({ queryKey: ['jornadas', eventId] });
  };

  const acao = useMutation({
    mutationFn: ({ id, qual }: { id: string; qual: string }) =>
      api(`/journeys/${id}/${qual}`, { method: 'POST' }),
    onSuccess: invalidar,
  });

  /**
   * Reabrir é a única ação desta tela que pede justificativa.
   *
   * Não é formalidade: o texto vai para o log de auditoria junto de quem clicou,
   * e é o que alguém vai ler daqui a um ano ao perguntar por que uma pessoa que
   * havia recusado recebeu convite de novo.
   */
  const reabrir = useMutation({
    mutationFn: ({ id, motivo }: { id: string; motivo?: string }) =>
      api(`/journeys/${id}/reabrir`, { method: 'POST', body: motivo ? { motivo } : {} }),
    onSuccess: () => {
      invalidar();
      setReabrindo(null);
      setMotivo('');
    },
  });

  const semJornada = resumo.data?.semJornada ?? 0;

  return (
    <>
      <PageHeader
        titulo="Jornadas"
        voltar={{ para: `/eventos/${eventId}`, texto: 'Voltar ao evento' }}
        subtitulo="Cada jornada é a conversa de um participante pelo WhatsApp."
        acoes={
          <>
            <button
              className="btn btn-secondary"
              onClick={() => {
                consulta.refetch();
                resumo.refetch();
              }}
              disabled={consulta.isFetching}
              title="Buscar de novo o estado de cada jornada"
            >
              <i className={`ti ti-refresh${consulta.isFetching ? ' girando' : ''}`} /> Atualizar
            </button>
            {pode('journeys.schedule') && (
              <button className="btn btn-secondary" onClick={() => setConfirmando('agendar')}>
                <i className="ti ti-clock-play" /> Agendar
              </button>
            )}
            {pode('journeys.start') && (
              <button className="btn btn-primary" onClick={() => setConfirmando('iniciar')}>
                <i className="ti ti-send" /> Iniciar jornadas
              </button>
            )}
          </>
        }
      />

      {acao.isError && <ErroBox erro={acao.error} />}

      {resumo.data && (
        <>
          <div className="resumo-import" style={{ marginBottom: 18 }}>
            {Object.entries(resumo.data.porEstado)
              .sort(([, a], [, b]) => b - a)
              .map(([estado, quantos]) => (
                <button
                  key={estado}
                  className="resumo-item"
                  style={{
                    cursor: 'pointer',
                    borderColor: filtro === estado ? 'var(--accent)' : undefined,
                  }}
                  onClick={() => {
                    setFiltro(filtro === estado ? '' : estado);
                    setPagina(1);
                  }}
                >
                  <div className="resumo-numero">{quantos}</div>
                  <div className="resumo-rotulo">{rotulo(estado)}</div>
                </button>
              ))}
          </div>

          {semJornada > 0 && (
            <div className="aviso" style={{ marginBottom: 16 }}>
              <i className="ti ti-user-question" />
              <span>
                <strong>{semJornada}</strong> participante(s) ativo(s) ainda sem jornada. Eles não
                receberam nada — nem receberão até alguém iniciar.
              </span>
            </div>
          )}
        </>
      )}

      {/* Aqui ficavam "Ritmo e horário de envio" e "Como a IA conduz a conversa".
          Saíram em 16/10/2026, por decisão do Gabriel: não são necessários
          agora. As colunas de `EventSettings` e as rotas continuam, e valem os
          padrões — sem janela de horário, um lembrete só, 24h depois do convite,
          até 10 perguntas, profundidade e modelo automáticos. Para voltar, os
          dois blocos estão no histórico do git, no commit desta remoção. */}

      {consulta.isPending && <Carregando />}
      {consulta.isError && <ErroBox erro={consulta.error} aoTentar={() => consulta.refetch()} />}

      {consulta.data &&
        (consulta.data.data.length === 0 ? (
          /*
            O filtro precisa de uma saída AQUI, e não só no chip.

            Os chips vêm de `porEstado`, que lista apenas os estados que têm
            jornada. Filtre por "Cancelada", inicie a jornada, e ela sai desse
            estado: o chip desaparece junto com ela, e não sobra nada para
            clicar e desligar o filtro. A tela fica vazia, dizendo que não há
            jornada nenhuma, com a jornada viva a um clique de distância — e a
            única saída era recarregar a página.
          */
          <Vazio
            icone="ti-route"
            titulo={filtro ? `Nenhuma jornada ${rotulo(filtro).toLowerCase()}` : 'Nenhuma jornada ainda'}
            descricao={
              filtro
                ? 'O filtro continua ligado. Se a jornada mudou de estado, ela saiu desta lista — mas continua existindo.'
                : 'A jornada nasce quando alguém a inicia. Antes disso, o participante cadastrado não recebe nada.'
            }
            acao={
              filtro ? (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setFiltro('');
                    setPagina(1);
                  }}
                >
                  Ver todas as jornadas
                </button>
              ) : undefined
            }
          />
        ) : (
          <div className="card" style={{ padding: 0 }}>
            <div className="tabela-envolvente">
              <table className="data-table">
                <thead>
                  <tr>
                    <th>Participante</th>
                    <th>Estado</th>
                    <th>Última troca</th>
                    <th>Lembrete</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {consulta.data.data.map((j) => (
                    <tr key={j.id}>
                      <td>
                        <div className="celula-principal">{j.participant.person.fullName}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                          {[j.participant.jobTitle, j.participant.company]
                            .filter(Boolean)
                            .join(' · ') || '—'}
                        </div>
                      </td>

                      <td>
                        <Tag type={ESTADOS[j.status]?.cor ?? 'muted'}>{rotulo(j.status)}</Tag>
                        {j.errorMessage && (
                          <div style={{ fontSize: 11, color: 'var(--danger)', marginTop: 4 }}>
                            {j.errorMessage}
                          </div>
                        )}
                        {j.schedule && !j.schedule.dispatchedAt && !j.schedule.cancelledAt && (
                          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                            para {new Date(j.schedule.scheduledFor).toLocaleString('pt-BR')}
                          </div>
                        )}
                      </td>

                      <td>
                        <UltimaTroca jornada={j} />
                      </td>

                      <td>
                        <Lembrete jornada={j} />
                      </td>

                      <td>
                        <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                          {j.acoes.includes('pausar') && pode('journeys.pause') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => acao.mutate({ id: j.id, qual: 'pausar' })}
                            >
                              Pausar
                            </button>
                          )}
                          {j.acoes.includes('retomar') && pode('journeys.pause') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => acao.mutate({ id: j.id, qual: 'retomar' })}
                            >
                              Retomar
                            </button>
                          )}
                          {j.acoes.includes('reentrada_autorizada') && pode('journeys.start') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              title="Autorizar um novo convite para quem saiu"
                              onClick={() =>
                                setReabrindo({ id: j.id, nome: j.participant.person.fullName })
                              }
                            >
                              Reabrir
                            </button>
                          )}
                          {j.acoes.includes('cancelar') && pode('journeys.cancel') && (
                            <button
                              className="btn btn-secondary btn-sm"
                              title="Cancelar jornada"
                              onClick={() => acao.mutate({ id: j.id, qual: 'cancelar' })}
                            >
                              <i className="ti ti-x" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ padding: 12 }}>
              <Paginacao meta={consulta.data.meta} aoMudar={setPagina} />
            </div>
          </div>
        ))}

      {confirmando && (
        <DisparoDeJornadas
          modo={confirmando}
          eventId={eventId}
          aoFechar={() => setConfirmando(null)}
        />
      )}

      {reabrindo && (
        <Modal
          titulo={`Reabrir a jornada de ${reabrindo.nome}`}
          descricao="Isto NÃO envia mensagem nenhuma. Só devolve a jornada para a fila, para que um novo convite possa ser disparado depois."
          aoFechar={() => {
            setReabrindo(null);
            setMotivo('');
          }}
          largura={520}
        >
          <Campo
            label="Como a pessoa pediu para voltar? (opcional)"
            name="motivo"
            linhas={3}
            autoFocus
            valor={motivo}
            aoMudar={setMotivo}
            placeholder="Ex.: ligou dizendo que tocou no botão errado e quer participar."
            ajuda="A auditoria já registra quem reabriu e quando. Isto acrescenta o porquê."
          />

          {/*
            O lembrete que importa, e que não é jurídico: quem recusou já disse
            não uma vez. O convite reaberto passa pela etapa 1 de novo, então a
            pessoa pode recusar outra vez num toque — e aí volta para cá. É esta
            frase, e não o campo de motivo, que sustenta a decisão de reabrir.
          */}
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 12 }}>
            O novo convite pede o consentimento outra vez. Se a pessoa recusar de novo, a
            jornada volta a ficar fechada na hora.
          </div>

          {reabrir.isError && (
            <div style={{ fontSize: 12, color: 'var(--danger)', marginTop: 10 }}>
              {(reabrir.error as Error).message}
            </div>
          )}

          <ModalRodape>
            <button
              className="btn btn-secondary"
              onClick={() => {
                setReabrindo(null);
                setMotivo('');
              }}
            >
              Cancelar
            </button>
            <button
              className="btn btn-primary"
              disabled={reabrir.isPending}
              onClick={() => reabrir.mutate({ id: reabrindo.id, motivo: motivo.trim() || undefined })}
            >
              {reabrir.isPending ? 'Reabrindo…' : 'Reabrir'}
            </button>
          </ModalRodape>
        </Modal>
      )}

    </>
  );
}
